/**
 * Build the "Manage subscription in Shopify" deep link.
 *
 * Dashbal bills through the Shopify Billing API (appSubscriptionCreate), NOT
 * Shopify Managed Pricing. That means there is NO Shopify-hosted pricing page
 * for the app — the deep link
 *   https://admin.shopify.com/store/<store>/charges/<app-handle>/pricing_plans
 * returns 404 (it only exists for Managed-Pricing apps). Plan changes,
 * upgrades and downgrades are driven from inside the embedded app's PlanPicker.
 *
 * So "Manage in Shopify" / "Change plan" must open the embedded app itself.
 * We use the same form already proven elsewhere in the app
 * (ConnectedChannelsView, the billing banner):
 *   https://<shop>.myshopify.com/admin/apps/<app-handle>
 * which Shopify resolves to the embedded app in admin for that store. This is
 * environment-agnostic: the correct app handle is injected per environment via
 * window.__DASHBAL_CONFIG__.shopifyAppStoreHandle (dashbal-p on stage-public,
 * dashbal on release/prod, dashbal-t on the custom stage app), and the shop
 * domain is the merchant's own store — so the same code is correct for prod
 * and non-prod.
 *
 * Inputs:
 *   - shopifyBillingShop: full myshopify.com domain (e.g. "dashbal-2.myshopify.com")
 *   - Returns null when the shop or the configured app handle is missing, so
 *     callers can render a conditional CTA instead of a broken link.
 */
export function buildShopifyManageBillingUrl(
    shopifyBillingShop: string | null | undefined,
): string | null {
    if (!shopifyBillingShop) return null;
    const config = (window as unknown as { __DASHBAL_CONFIG__?: { shopifyAppStoreHandle?: string } }).__DASHBAL_CONFIG__;
    const appHandle = config?.shopifyAppStoreHandle;
    if (!appHandle) return null;
    // Normalize: accept either a bare domain or one that already has a scheme.
    const shopDomain = shopifyBillingShop.replace(/^https?:\/\//, "");
    if (!shopDomain) return null;
    return `https://${shopDomain}/admin/apps/${appHandle}`;
}
